import { Navigate, Route, createBrowserRouter, createRoutesFromElements } from 'react-router-dom';

import { withAuth } from './components/require-auth';
import withTitle from './components/with-title';
import {
	withApprovedWPCloudPartnership,
	withFeatureFlag,
	withFeatureFlagsOrAutomattician,
	withWPCloudPartnership,
	withWPCloudRole,
} from './components/wpcloud-partnership-status';
import { pipe, suspend } from './lib/routing';

import Error from './sections/error';
import Home from './sections';
import Auth from './sections/auth';
import AuthToken from './sections/auth/token';
import Logout from './components/logout';
import { FallbackContent, FallbackLayout, withFallback } from './components/layout/fallback';

const Overview = suspend(
	() => import( /* webpackChunkName: "overview" */ './sections/overview' ),
	<FallbackLayout />,
);

const WPCloudSignup = suspend(
	() => import( /* webpackChunkName: "wpcloud-noauth" */ './sections/wpcloud/signup' ),
	<FallbackLayout />,
);
const WPCloudPricing = suspend(
	() => import( /* webpackChunkName: "wpcloud-noauth" */ './sections/wpcloud/signup/pricing' ),
	<FallbackLayout />,
);

const WPCloudPaymentMethods = suspend(
	() => import( /* webpackChunkName: "wpcloud" */ './sections/wpcloud/billing/payment-methods' ),
	<FallbackLayout />,
);

const WPCloudPaymentMethodForm = suspend(
	() => import( /* webpackChunkName: "wpcloud" */ './sections/wpcloud/billing/payment-method-form' ),
	<FallbackLayout />,
);

const WPCloud = suspend(
	() => import( /* webpackChunkName: "wpcloud" */ './sections/wpcloud' ),
	<FallbackLayout />,
);
const WPCloudPartnershipDetails = suspend(
	() => import( /* webpackChunkName: "wpcloud" */ './sections/wpcloud/partnership-details' ),
	<FallbackLayout />,
);
const WPCloudBilling = suspend(
	() => import( /* webpackChunkName: "wpcloud" */ './sections/wpcloud/billing' ),
	<FallbackLayout />,
);
const WPCloudBillingSummary = suspend(
	() => import( /* webpackChunkName: "wpcloud" */ './sections/wpcloud/billing/summary' ),
	<FallbackLayout />,
);
const WPCloudBillingInvoices = suspend(
	() => import( /* webpackChunkName: "wpcloud" */ './sections/wpcloud/billing/invoices' ),
	<FallbackLayout />,
);
const WPCloudBillingInvoiceUsageReport = suspend(
	() => import( /* webpackChunkName: "wpcloud" */ './sections/wpcloud/billing/invoices/invoice-usage-report' ),
	<FallbackLayout />,
);
const WPCloudBillingPricing = suspend(
	() => import( /* webpackChunkName: "wpcloud" */ './sections/wpcloud/billing/pricing' ),
	<FallbackLayout />,
);
const WPCloudOverview = suspend(
	() => import( /* webpackChunkName: "wpcloud" */ './sections/wpcloud/overview' ),
	<FallbackContent />,
);
const WPCloudInventory = suspend(
	() => import( /* webpackChunkName: "wpcloud" */ './sections/wpcloud/inventory' ),
	<FallbackContent />,
);
const WPCloudRequestAPIAccess = suspend(
	() => import( /* webpackChunkName: "wpcloud" */ './sections/wpcloud/request-api-access' ),
	<FallbackContent />,
);
const WPCloudAPIKeys = suspend(
	() => import( /* webpackChunkName: "wpcloud" */ './sections/wpcloud/api-keys' ),
	<FallbackContent />,
);
const WPCloudSupportForm = suspend(
	() => import( /* webpackChunkName: "wpcloud" */ './sections/wpcloud/support-request' ),
	<FallbackContent />,
);
const WPCloudSiteMetrics = suspend(
	() => import( /* webpackChunkName: "wpcloud" */ './sections/wpcloud/site-metrics' ),
	<FallbackContent />,
);
const WPCloudTeam = suspend(
	() => import( /* webpackChunkName: "wpcloud" */ './sections/wpcloud/team' ),
	<FallbackContent />,
);
const WPCloudTeamAcceptInvite = suspend(
	() => import( /* webpackChunkName: "wpcloud" */ './sections/wpcloud/team/accept-invite' ),
	<FallbackLayout />,
);
const WPCloudSitePerformance = suspend(
	() => import( /* webpackChunkName: "wpcloud" */ './sections/wpcloud/site-performance' ),
	<FallbackContent />,
);
const WPCloudAssistant = suspend(
	() => import( /* webpackChunkName: "wpcloud" */ './sections/wpcloud/assistant' ),
	<FallbackContent />,
);
const WPCloudWarden = suspend(
	() => import( /* webpackChunkName: "wpcloud" */ './sections/wpcloud/warden' ),
	<FallbackContent />,
);
const Jetpack = suspend(
	() => import( /* webpackChunkName: "jetpack" */ './sections/jetpack' ),
	<FallbackLayout />,
);

const JetpackAccount = suspend(
	() => import( /* webpackChunkName: "jetpack" */ './sections/jetpack/account' ),
	<FallbackContent />,
);

const JetpackLicenses = suspend(
	() => import( /* webpackChunkName: "jetpack" */ './sections/jetpack/licenses' ),
	<FallbackContent />,
);

export default createBrowserRouter(
	createRoutesFromElements(
		<>
			<Route path="/" Component={ Home } ErrorBoundary={ Error }>
				<Route index Component={ pipe( withAuth, withTitle( 'Partnerships' ) )( Overview ) } />

				{/* WP Cloud Signup */ }
				<Route
					path="wpcloud/signup"
					Component={
						pipe(
							withFallback( FallbackLayout ),
							withAuth,
							withFeatureFlag( 'wpcloud-signup-section', '/' ),
							withWPCloudPartnership( 'not_started' ),
							withTitle( 'WP Cloud Partnership Application' ),
						)( WPCloudSignup )
					}
				/>

				<Route
					path="wpcloud/signup/pricing"
					Component={
						pipe(
							withFallback( FallbackLayout ),
							withAuth,
							withFeatureFlag( 'wpcloud-signup-section', '/' ),
							withWPCloudPartnership( 'incomplete' ),
							withTitle( 'WP Cloud Partnership Application' ),
						)( WPCloudPricing )
					}
				/>

				{/* WP Cloud Billing */ }
				<Route path="wpcloud/billing" Component={ pipe( withAuth )( WPCloudBilling ) }>
					<Route
						index
						Component={
							pipe(
								withFallback( FallbackContent ),
								withAuth,
								withWPCloudPartnership( 'complete' ),
								withWPCloudRole( [ 'owner' ] ),
								withTitle( 'WP Cloud Billing Summary' ),
							)( WPCloudBillingSummary )
						}
					/>

					<Route
						path="invoices"
						Component={
							pipe(
								withFallback( FallbackContent ),
								withAuth,
								withWPCloudPartnership( 'complete' ),
								withWPCloudRole( [ 'owner' ] ),
								withTitle( 'WP Cloud Invoices' ),
							)( WPCloudBillingInvoices )
						}
					/>

					<Route
						path="invoices"

					>
						<Route
							index
							Component={
								pipe(
									withFallback( FallbackContent ),
									withAuth,
									withWPCloudPartnership( 'complete' ),
									withWPCloudRole( [ 'owner' ] ),
									withTitle( 'WP Cloud Invoices' ),
								)( WPCloudBillingInvoices )
							}
						/>
						<Route
							path=":invoiceId/usage-report"
							Component={
								pipe(
									withFallback( FallbackContent ),
									withAuth,
									withWPCloudPartnership( 'complete' ),
									withWPCloudRole( [ 'owner' ] ),
									withTitle( 'WP Cloud Invoices' ),
								)( WPCloudBillingInvoiceUsageReport )
							}
						/>
					</Route>

					<Route path="payment-methods">
						<Route
							index
							Component={
								pipe(
									withFallback( FallbackContent ),
									withAuth,
									withFeatureFlag( 'wpcloud-payment-methods', '/' ),
									withWPCloudPartnership( 'complete' ),
									withWPCloudRole( [ 'owner' ] ),
									withTitle( 'WP Cloud Payment Methods' ),
								)( WPCloudPaymentMethods )
							}
						/>

						<Route
							path="add"
							Component={
								pipe(
									withFallback( FallbackContent ),
									withAuth,
									withFeatureFlag( 'wpcloud-payment-methods', '/' ),
									withApprovedWPCloudPartnership,
									withWPCloudRole( [ 'owner' ] ),
									withTitle( 'WP Cloud Payment Methods' ),
								)( WPCloudPaymentMethodForm )
							}
						/>
					</Route>

					<Route
						path="pricing"
						Component={
							pipe(
								withFallback( FallbackContent ),
								withAuth,
								withWPCloudPartnership( 'complete' ),
								withWPCloudRole( [ 'owner' ] ),
								withTitle( 'WP Cloud Pricing' ),
							)( WPCloudBillingPricing )
						}
					/>
				</Route>

				{/* WP Cloud */ }
				<Route path="wpcloud" Component={ pipe( withAuth )( WPCloud ) }>
					<Route
						index
						Component={
							pipe(
								withFallback( FallbackContent ),
								withAuth,
								withWPCloudPartnership( 'complete' ),
								withTitle( 'WP Cloud Overview' ),
							)( WPCloudOverview )
						}
					/>

					<Route
						path="partnership-details"
						Component={
							pipe(
								withFallback( FallbackContent ),
								withAuth,
								withWPCloudPartnership( 'complete' ),
								withWPCloudRole( [ 'owner' ] ),
								withTitle( 'WP Cloud Partnership Details' ),
							)( WPCloudPartnershipDetails )
						}
					/>

					<Route
						path="inventory"
						Component={
							pipe(
								withFallback( FallbackContent ),
								withAuth,
								withFeatureFlag( 'wpcloud-inventory-section', '/wpcloud' ),
								withApprovedWPCloudPartnership,
								withTitle( 'WP Cloud Inventory' ),
							)( WPCloudInventory )
						}
					/>

					<Route
						path="api-keys"
						Component={
							pipe(
								withFallback( FallbackContent ),
								withAuth,
								withFeatureFlag( 'wpcloud-api-keys-section', '/wpcloud' ),
								withApprovedWPCloudPartnership,
								withWPCloudRole( [ 'owner' ] ),
								withTitle( 'WP Cloud API Keys' ),
							)( WPCloudAPIKeys )
						}
					/>

					<Route
						path="request-api-access"
						Component={
							pipe(
								withFallback( FallbackContent ),
								withAuth,
								withFeatureFlag( 'wpcloud-request-api-access-section', '/wpcloud' ),
								withApprovedWPCloudPartnership,
								withWPCloudRole( [ 'owner', 'manager' ] ),
								withTitle( 'WP Cloud API Access' ),
							)( WPCloudRequestAPIAccess )
						}
					/>

					<Route
						path="support-request"
						Component={
							pipe(
								withFallback( FallbackContent ),
								withAuth,
								withFeatureFlag( 'wpcloud-support-request-section', '/wpcloud' ),
								withApprovedWPCloudPartnership,
								withWPCloudRole( [ 'owner', 'manager' ] ),
								withTitle( 'WP Cloud Support' ),
							)( WPCloudSupportForm )
						}
					/>

					<Route
						path="site-metrics"
						Component={
							pipe(
								withFallback( FallbackContent ),
								withAuth,
								withFeatureFlag( 'wpcloud-site-metrics-section', '/wpcloud' ),
								withApprovedWPCloudPartnership,
								withTitle( 'WP Cloud Insights' ),
							)( WPCloudSiteMetrics )
						}
					/>

					<Route
						path="assistant"
						Component={
							pipe(
								withFallback( FallbackContent ),
								withAuth,
								withFeatureFlagsOrAutomattician(
									[ 'wpcloud-assistant-section', 'wpcloud-agenttic-ui' ],
									'/wpcloud',
								),
								withTitle( 'WP Cloud AI Assistant' ),
							)( WPCloudAssistant )
						}
					/>

					<Route
						path="team"
						Component={
							pipe(
								withFallback( FallbackContent ),
								withAuth,
								withFeatureFlag( 'wpcloud-team-section', '/wpcloud' ),
								withApprovedWPCloudPartnership,
								withWPCloudRole( [ 'owner', 'manager' ] ),
								withTitle( 'WP Cloud Team' ),
							)( WPCloudTeam )
						}
					/>

					{ /* WP Cloud Site Performance — /wpcloud/site/:siteId/[tab] */ }
					<Route
						path="warden"
						Component={
							pipe(
								withFallback( FallbackContent ),
								withAuth,
								withApprovedWPCloudPartnership,
								withTitle( 'WP Cloud Site Anomalies' ),
							)( WPCloudWarden )
						}
					/>

					<Route
						path="site"
						element={ <Navigate to="/wpcloud/site-metrics" replace /> }
					/>

					<Route
						path="site/:siteId/*"
						Component={
							pipe(
								withFallback( FallbackContent ),
								withAuth,
								withFeatureFlag( 'wpcloud-site-metrics-section', '/wpcloud' ),
								withApprovedWPCloudPartnership,
								withTitle( 'WP Cloud Site Performance' ),
							)( WPCloudSitePerformance )
						}
					/>
				</Route>

				{/* WP Cloud Team — Invite Accept (no partnership required — invitee may not be a partner yet) */ }
				<Route
					path="wpcloud/team/invite/accept"
					Component={
						pipe(
							withFallback( FallbackLayout ),
							withAuth,
							withTitle( 'Accept WP Cloud Invite' ),
						)( WPCloudTeamAcceptInvite )
					}
				/>

				{/* Jetpack */ }
				<Route path="jetpack" Component={ pipe( withAuth )( Jetpack ) }>
					<Route
						index
						Component={ pipe(
							withAuth,
							withTitle( 'Jetpack Partner Keys' ),
						)( JetpackAccount ) }
					/>

					<Route
						path="licenses"
						Component={ pipe(
							withAuth,
							withTitle( 'Jetpack Licenses' ),
						)( JetpackLicenses ) }
					/>
				</Route>
			</Route>

			{/* Authentication */ }
			<Route path="/auth/token" Component={ AuthToken } ErrorBoundary={ Error } />
			<Route path="/auth" Component={ Auth } ErrorBoundary={ Error } />
			<Route path="/logout" Component={ Logout } ErrorBoundary={ Error } />
		</>,
	),
);
